/**
 * TENDERS — FROM THE NOTICE TO THE AWARD.
 *
 * Two facts this screen refuses to soften.
 *
 * THE DEADLINE IS AN INSTANT WITH A ZONE. "15:00" in a notice is not 15:00 on
 * our clock, and a tender missed by an hour is a tender lost. The stated zone is
 * kept beside the instant and shown with it, always.
 *
 * THE ROAD IS ONE WAY. A tender moves through a closed set of states and the
 * database is the only door (sales.move_tender). The screen offers exactly the
 * moves that are legal from where it stands, so an illegal one is not something
 * a person discovers by being refused.
 *
 * A bid submitted after the deadline is recorded — with a written override that
 * is kept — because pretending it was on time is worse than recording that it
 * was not. EMD is recorded and never posted as money (B-M3-2).
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun, go } from '../hooks.js';
import { Banner, Button, Card, Loading, Money, Pill, Register, Select, Text, TextArea, Check, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { CustomerPickerById } from '../pickers.js';
import { count, date, instant, today, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';
import type { RequirementLine, FiledPaper, TenderLine, TenderDeliveryConditions, TenderContact } from '../../../src/views/tenders.js';
import type { BidReadiness } from '../../../src/sales/tenderReadiness.js';
import type { AttachmentCategory, RequirementCategory } from '../../../src/sales/tenders.js';
import { LineEditor, emptyLine, toLineBodies, type LineRow, type SkuOption } from '../lines.js';
import type { SkuRow } from '../../../src/views/masters.js';

const MESSAGES = screenById('tenders')!.messages;

/** The button that moves a tender on says what it DOES, not the state it lands in. */
function moveWords(to: string): string {
  switch (to) {
    case 'bidding': return 'Start bidding'; case 'submitted': return 'Mark submitted'; case 'won': return 'Mark won'; case 'lost': return 'Mark lost';
    case 'withdrawn': return 'Withdraw'; case 'cancelled': return 'Mark cancelled'; default: return words(to);
  }
}

/** Where a tender may go from where it is. The database holds the same road; this is so the screen never offers a refused move. */
const ROAD: Record<string, string[]> = {
  watching:  ['bidding', 'withdrawn', 'cancelled'],
  bidding:   ['withdrawn', 'cancelled'],   // 'submitted' is reached by recording the bid (M3-10), never by being told so
  submitted: ['won', 'lost', 'cancelled'],
  won: [], lost: [], withdrawn: [], cancelled: [],
};

export function Tenders({ id, session }: { id: string | null; session: Session }) {
  if (id === 'new') return <NewTender />;
  if (id) return <OneTender tenderId={Number(id)} session={session} />;
  return <TenderRegister session={session} />;
}

function TenderRegister({ session }: { session: Session }) {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('tenders.list', { ...(status ? { status } : {}), page, pageSize: 50 }), [status, page], MESSAGES);

  const columns: readonly Column<{ tenderId: number; buyerName: string; reference: string; title: string; closesAt: string; closesTz: string; status: string; emdAmountPaise: number | null; bids: number; closed: boolean; awardedValuePaise: number | null }>[] = [
    { key: 'ref', head: 'Reference', cell: (r) => <><strong>{r.reference}</strong><div className="muted">{r.title}</div></> },
    { key: 'buyer', head: 'Who is tendering', cell: (r) => r.buyerName },
    { key: 'closes', head: 'Closes', cell: (r) => <>{instant(r.closesAt)}<div className="muted">{r.closesTz}{r.closed ? ' · closed' : ''}</div></> },
    { key: 'emd', head: 'EMD', right: true, cell: (r) => r.emdAmountPaise === null ? <span className="muted">—</span> : <Money paise={r.emdAmountPaise} /> },
    { key: 'bids', head: 'Offers sent', right: true, cell: (r) => count(r.bids) },
    { key: 'status', head: 'Status', cell: (r) => <><Pill tone={r.status === 'won' ? 'good' : r.status === 'lost' ? 'danger' : r.status === 'submitted' ? 'primary' : 'empty'}>{r.status}</Pill>
        {r.awardedValuePaise !== null ? <div className="muted"><Money paise={r.awardedValuePaise} /></div> : null}</> },
  ];

  return (
    <Page title="Tenders" sub="What is out there, when it closes, what we sent, and how it ended."
          actions={can(session, 'tender.manage') ? <Button kind="primary" onClick={() => go('#/tenders/new')}>Record a tender</Button> : null}>
      <div className="toolbar">
        <Select name="status" label="Status" value={status} onChange={(v) => { setStatus(v); setPage(1); }} placeholder="Any status"
                options={['watching', 'bidding', 'submitted', 'won', 'lost', 'withdrawn', 'cancelled'].map((v) => ({ value: v, label: words(v) }))} />
      </div>
      <Card>
        {asked.loading ? <Loading what="tenders" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows} empty="No tender matches that."
                        onOpen={(r) => go(`#/tenders/${r.tenderId}`)} asOf={new Date().toISOString()} />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
    </Page>
  );
}

function OneTender({ tenderId, session }: { tenderId: number; session: Session }) {
  const asked = useAsk(() => api.view('tenders.get', { tenderId }), [tenderId], MESSAGES);
  // 0065 — the checklist and the papers, each its own ask so one refusal does not blank the other
  const checklist = useAsk(() => api.view('tenders.requirements', { tenderId }), [tenderId], MESSAGES);
  const mayReadPapers = can(session, 'view.tender_documents');
  const papers = useAsk(() => (mayReadPapers ? api.view('tenders.documents', { tenderId }) : Promise.resolve({ files: [], live: 0 })), [tenderId, mayReadPapers], MESSAGES);
  const skus = useAsk(() => api.view('skus.list', { active: true, pageSize: 200 }), [], MESSAGES);
  const gases = useAsk(() => api.view('gases.list', {}), [], MESSAGES);
  const [moving, setMoving] = useState<string | null>(null);
  const [bidding, setBidding] = useState(false);
  const [correcting, setCorrecting] = useState(false);

  if (asked.loading) return <Page title="Tender"><Loading what="the tender" /></Page>;
  if (asked.error) return <Page title="Tender"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Page>;
  const d = asked.data!;
  const t = d.tender;
  const may = can(session, 'tender.manage');
  const next = ROAD[t.status] ?? [];
  const readiness = checklist.data?.readiness ?? d.readiness;
  const bid = bidButton(readiness);
  const reloadAll = () => { asked.reload(); checklist.reload(); papers.reload(); };

  return (
    <Page title={t.reference} sub={`${t.title} · ${t.buyerName}`}
          actions={<>
            {t.buyerCustomerId ? <Button onClick={() => go(`#/customers/${t.buyerCustomerId}`)}>The buyer</Button> : null}
            {can(session, 'quote.draft') && t.buyerCustomerId && (t.status === 'watching' || t.status === 'bidding')
              ? <Button onClick={() => go(`#/quotes/new?tender=${tenderId}&customer=${t.buyerCustomerId}`)}>Draft the bid</Button> : null}
            {may && t.status === 'bidding' ? <Button kind="primary" disabled={bid.disabled} onClick={() => setBidding((v) => !v)}>{bidding ? 'Not yet' : bid.label}</Button> : null}
            {may && next.length > 0 ? <Button onClick={() => setCorrecting((v) => !v)}>{correcting ? 'Leave it' : 'Correct the notice'}</Button> : null}
            {may && next.map((to) => (
              <Button key={to} kind={to === 'won' ? 'good' : to === 'lost' || to === 'cancelled' ? 'danger' : 'ghost'}
                      onClick={() => setMoving(moving === to ? null : to)}>{moveWords(to)}</Button>
            ))}
          </>}>

      {next.length === 0 ? (
        <Banner kind="info" title={`This tender is ${words(t.status)}, and the road ends there.`} detail="Nothing more is recorded against it." />
      ) : null}
      {t.closed && t.status === 'bidding' ? (
        <Banner kind="warn" title="The deadline has passed and no bid is recorded."
                detail="A submission after the deadline can still be recorded, with a written override that is kept — it is not quietly treated as on time." />
      ) : null}

      {t.status === 'watching' || t.status === 'bidding' ? <ReadinessNote readiness={readiness} /> : null}

      {moving ? <MoveTender tenderId={tenderId} to={moving} onDone={() => { setMoving(null); reloadAll(); }} /> : null}
      {correcting ? <CorrectTender tender={t} onDone={() => { setCorrecting(false); asked.reload(); }} /> : null}
      {bidding ? <SubmitBid tenderId={tenderId} customerId={t.buyerCustomerId} closesAt={t.closesAt} readiness={readiness} onDone={() => { setBidding(false); reloadAll(); }} /> : null}

      {checklist.loading ? <Card title="What the notice asks for"><Loading what="the checklist" /></Card>
        : checklist.error ? <Card title="What the notice asks for"><Banner kind="error" title={checklist.error.title} {...(checklist.error.detail ? { detail: checklist.error.detail } : {})} /></Card>
        : <RequirementsPanel tenderId={tenderId} lines={checklist.data!.lines} readiness={checklist.data!.readiness}
                             may={can(session, 'tender.requirements') && next.length > 0} onChanged={reloadAll} />}

      {mayReadPapers ? (
        papers.loading ? <Card title="The papers"><Loading what="the papers" /></Card>
        : papers.error ? <Card title="The papers"><Banner kind="error" title={papers.error.title} {...(papers.error.detail ? { detail: papers.error.detail } : {})} /></Card>
        : <DocumentsPanel tenderId={tenderId} files={papers.data!.files} live={papers.data!.live} lines={checklist.data?.lines ?? []}
                          may={can(session, 'tender.documents')} onChanged={reloadAll} />
      ) : null}

      <TenderDemandPanel tenderId={tenderId} lines={d.lines} skus={skus.data?.rows ?? []} gases={gases.data?.rows ?? []}
                         may={can(session, 'tender.set_lines') && next.length > 0} onChanged={reloadAll} />

      <div className="grid two">
        <TenderDeliveryPanel tenderId={tenderId} conditions={d.deliveryConditions} buyerCustomerId={t.buyerCustomerId} session={session}
                             may={can(session, 'tender.set_delivery_conditions') && next.length > 0} onChanged={reloadAll} />
        <TenderContactsPanel tenderId={tenderId} contacts={d.contacts}
                             may={can(session, 'tender.contacts') && next.length > 0} onChanged={reloadAll} />
      </div>

      <div className="grid two">
        <Card title="The notice">
          <div className="detail-grid">
            <div><div className="k">Closes</div><div className="v"><strong>{instant(t.closesAt)}</strong><div className="muted">stated as {t.closesTz}</div></div></div>
            <div><div className="k">Published</div><div className="v">{date(t.publishedOn)}</div></div>
            <div><div className="k">Status</div><div className="v"><Pill tone={t.status === 'won' ? 'good' : t.status === 'lost' ? 'danger' : 'primary'}>{t.status}</Pill></div></div>
            {t.awardedValuePaise !== null ? <div><div className="k">Awarded at</div><div className="v"><Money paise={t.awardedValuePaise} /></div></div> : null}
          </div>
          {t.scope ? <p className="muted" style={{ marginBottom: 0 }}>{t.scope}</p> : null}
        </Card>

        <Card title="Earnest money (EMD)" sub="recorded here; it never goes on the customer's account">
          {t.emdAmountPaise === null ? <p className="muted" style={{ marginBottom: 0 }}>No EMD is recorded for this tender.</p> : (
            <div className="detail-grid">
              <div><div className="k">Amount</div><div className="v"><Money paise={t.emdAmountPaise} /></div></div>
              <div><div className="k">Instrument</div><div className="v">{words(t.emdInstrument)}</div></div>
              <div><div className="k">Reference</div><div className="v">{t.emdReference ?? '—'}</div></div>
            </div>
          )}
        </Card>
      </div>

      <Card title="What we sent" sub="which piece of paper the buyer actually holds, and when it reached them">
        <Register
          columns={[
            { key: 'q', head: 'Quotation', cell: (b: { quotationNumber: string | null; quotationId: number }) => <a href={`#/quotes/${b.quotationId}`}>{b.quotationNumber ?? `#${b.quotationId}`}</a> },
            { key: 'total', head: 'Offered at', right: true, cell: (b: { totalPaise: number }) => <Money paise={b.totalPaise} /> },
            { key: 'at', head: 'Submitted', cell: (b: { submittedAt: string; recordedLate: boolean }) => <>{b.submittedAt}{b.recordedLate ? <div><Pill tone="danger">after the deadline</Pill></div> : null}</> },
            { key: 'ack', head: 'Their acknowledgement', cell: (b: { acknowledgement: string | null }) => b.acknowledgement ?? <span className="muted">none</span> },
            { key: 'why', head: 'Override', cell: (b: { lateOverrideNote: string | null; requirementsOverrideNote: string | null }) =>
                b.lateOverrideNote || b.requirementsOverrideNote
                  ? <>{b.lateOverrideNote ? <div>late: {b.lateOverrideNote}</div> : null}{b.requirementsOverrideNote ? <div>checklist: {b.requirementsOverrideNote}</div> : null}</>
                  : <span className="muted">—</span> },
          ]}
          rows={d.bids} empty="Nothing has been sent against this tender." />
      </Card>

      <Card title="Every move, and why" sub="who moved it, from what to what, on which day">
        <ul className="timeline">
          {d.history.map((h, i) => (
            <li key={i}><strong>{words(h.fromStatus)} → {words(h.toStatus)}</strong> by user {h.actor} — {h.reason}
              <div className="when">{h.at}</div></li>
          ))}
        </ul>
        {d.history.length === 0 ? <p className="muted" style={{ marginBottom: 0 }}>It has not moved since it was recorded.</p> : null}
      </Card>
    </Page>
  );
}

/**
 * THE NOTICE AS WE FIRST READ IT IS OFTEN NOT THE NOTICE. Buyers publish
 * corrigenda: the deadline moves, the scope changes, the EMD is revised. That
 * is a correction to what we recorded, not a move along the road, and it is
 * kept apart from the status history for exactly that reason.
 */
function CorrectTender({ tender, onDone }: {
  tender: { tenderId: number; buyerName: string; reference: string; title: string; scope: string | null; closesAt: string; closesTz: string; emdAmountPaise: number | null; emdReference: string | null };
  onDone: () => void;
}) {
  const [closesAt, setClosesAt] = useState(tender.closesAt);
  const [closesTz, setClosesTz] = useState(tender.closesTz);
  const [title, setTitle] = useState(tender.title);
  const [scope, setScope] = useState(tender.scope ?? '');
  const [emd, setEmd] = useState(tender.emdAmountPaise === null ? '' : (tender.emdAmountPaise / 100).toFixed(2));
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);

  const changed = closesAt !== tender.closesAt || closesTz !== tender.closesTz || title !== tender.title
    || scope !== (tender.scope ?? '') || emd !== (tender.emdAmountPaise === null ? '' : (tender.emdAmountPaise / 100).toFixed(2));

  async function submit() {
    const ok = await runner.run(() => api.command('tender.correct', {
      tenderId: tender.tenderId,
      ...(closesAt !== tender.closesAt ? { closesAt } : {}),
      ...(closesTz !== tender.closesTz ? { closesTz: closesTz.trim() } : {}),
      ...(title !== tender.title ? { title: title.trim() } : {}),
      ...(scope !== (tender.scope ?? '') ? { scope: scope.trim() } : {}),
      ...(emd !== (tender.emdAmountPaise === null ? '' : (tender.emdAmountPaise / 100).toFixed(2))
          ? { emdAmountPaise: emd.trim() ? Math.round(Number(emd.replace(/[^0-9.]/g, '')) * 100) : null } : {}),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    }));
    if (ok) onDone();
  }

  return (
    <Card title="Correct the notice" sub="a corrigendum from the buyer — the deadline moved, the scope changed, the EMD was revised">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="info" title="This corrects what we recorded. It is not a move along the road."
              detail="Where the tender stands — watching, bidding, submitted — is unchanged, and the status history stays a history of decisions rather than of typing." />
      <div className="grid two">
        <div>
          <Text name="fix-closes" label="Closes at" value={closesAt} onChange={setClosesAt} required
                help="the instant, with its offset" />
          <Text name="fix-tz" label="Stated timezone" value={closesTz} onChange={setClosesTz} required />
          <Text name="fix-emd" label="EMD (₹)" value={emd} onChange={setEmd} help="blank removes it" />
        </div>
        <div>
          <Text name="fix-title" label="What it is for" value={title} onChange={setTitle} required />
          <TextArea name="fix-scope" label="Scope" value={scope} onChange={setScope} />
          <TextArea name="fix-reason" label="Why it is being corrected" value={reason} onChange={setReason}
                    help="the corrigendum number, usually" />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !changed}>
        {runner.busy ? 'Correcting…' : changed ? 'Save the correction' : 'Nothing has changed'}
      </Button>
    </Card>
  );
}

function MoveTender({ tenderId, to, onDone }: { tenderId: number; to: string; onDone: () => void }) {
  const [reason, setReason] = useState(''); const [rupees, setRupees] = useState('');
  const runner = useRun(MESSAGES);
  const needsValue = to === 'won';
  async function submit() {
    const ok = await runner.run(() => api.command('tender.move', {
      tenderId, to: to as 'watching' | 'bidding' | 'submitted' | 'won' | 'lost' | 'withdrawn' | 'cancelled',
      reason: reason.trim(),
      ...(needsValue && rupees.trim() ? { awardedValuePaise: Math.round(Number(rupees.replace(/[^0-9.]/g, '')) * 100) } : {}),
    }));
    if (ok) onDone();
  }
  return (
    <Card title={`Move it to ${words(to)}`} sub="the move and the reason are both kept">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {needsValue ? <Text name="awarded" label="Awarded at (₹)" value={rupees} onChange={setRupees} required /> : null}
      <TextArea name="move-reason" label="Why" value={reason} onChange={setReason} />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !reason.trim() || (needsValue && !rupees.trim())}>
        {runner.busy ? 'Moving…' : `Move it to ${words(to)}`}
      </Button>
    </Card>
  );
}

function SubmitBid({ tenderId, customerId, closesAt, readiness, onDone }: { tenderId: number; customerId: number | null; closesAt: string; readiness: BidReadiness; onDone: () => void }) {
  const [quotationId, setQuotationId] = useState('');
  const [submittedAt, setSubmittedAt] = useState(new Date().toISOString().slice(0, 19) + 'Z');
  const [ack, setAck] = useState(''); const [note, setNote] = useState(''); const [late, setLate] = useState('');
  const [override, setOverride] = useState('');
  const runner = useRun(MESSAGES);
  const quotes = useAsk(() => api.view('quotes.list', { ...(customerId ? { customerId } : {}), status: 'issued', pageSize: 100 }), [customerId], MESSAGES);
  const isLate = submittedAt > closesAt;
  const needsOverride = readiness.needsOverride;

  async function submit() {
    const ok = await runner.run((retryKey) => api.command('tender.submitBid', {
      tenderId, quotationId: Number(quotationId), submittedAt,
      ...(ack.trim() ? { acknowledgement: ack.trim() } : {}),
      ...(isLate && late.trim() ? { lateOverrideNote: late.trim() } : {}),
      ...(needsOverride && override.trim() ? { requirementsOverrideReason: override.trim() } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) onDone();
  }

  return (
    <Card title="Record the bid" sub="which issued offer was sent, and the instant it was sent at">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {needsOverride ? (
        <Banner kind="warn" title={`Mandatory requirement${readiness.mandatoryExceptions.length > 1 ? 's' : ''} ${readiness.mandatoryExceptions.map((x) => x.requirementNo).join(', ')} stand${readiness.mandatoryExceptions.length > 1 ? '' : 's'} as an exception.`}
                detail="The bid can go out only with a written override, which is kept on the bid, and only by someone holding the override function." />
      ) : null}
      {isLate ? (
        <Banner kind="warn" title="This is after the deadline."
                detail={`The notice closes at ${closesAt}. It can still be recorded — with the override below, which is kept and shown on the tender for ever.`} />
      ) : null}
      <div className="grid two">
        <div>
          <Select name="bid-quote" label="Which offer was sent" value={quotationId} onChange={setQuotationId} placeholder="Choose an issued quotation"
                  options={(quotes.data?.rows ?? []).map((q) => ({ value: String(q.quotationId), label: `${q.number ?? `#${q.quotationId}`} — ${(q.totalPaise / 100).toFixed(2)}` }))} />
          <Text name="bid-at" label="Submitted at" value={submittedAt} onChange={setSubmittedAt} required
                help="an instant with its zone, e.g. 2026-09-20T14:55:00+05:30" />
        </div>
        <div>
          <Text name="bid-ack" label="Their acknowledgement" value={ack} onChange={setAck} />
          <TextArea name="bid-note" label="Note" value={note} onChange={setNote} />
        </div>
      </div>
      {isLate ? <TextArea name="bid-late" label="Why a late submission is being recorded" value={late} onChange={setLate} /> : null}
      {needsOverride ? <TextArea name="bid-override" label="Why the bid goes out with the exception" value={override} onChange={setOverride}
                                 help="at least ten characters; it stays on the bid" /> : null}
      <Button kind="primary" onClick={() => { void submit(); }}
              disabled={runner.busy || !quotationId || !submittedAt || (isLate && !late.trim()) || (needsOverride && override.trim().length < 10)}>
        {runner.busy ? 'Recording…' : isLate ? 'Record it as late' : 'Record the bid'}
      </Button>
    </Card>
  );
}

function NewTender() {
  const [buyerName, setBuyerName] = useState(''); const [buyerCustomerId, setBuyerCustomerId] = useState('');
  const [reference, setReference] = useState(''); const [title, setTitle] = useState(''); const [scope, setScope] = useState('');
  const [publishedOn, setPublishedOn] = useState(''); const [closesAt, setClosesAt] = useState('');
  const [closesTz, setClosesTz] = useState('Asia/Kolkata');
  const [emd, setEmd] = useState(''); const [emdInstrument, setEmdInstrument] = useState(''); const [emdRef, setEmdRef] = useState('');
  const runner = useRun(MESSAGES);

  async function submit() {
    const ok = await runner.run(async () => {
      const r = await api.command('tender.record', {
        buyerName: buyerName.trim(), reference: reference.trim(), title: title.trim(),
        closesAt, closesTz: closesTz.trim(),
        ...(buyerCustomerId ? { buyerCustomerId: Number(buyerCustomerId) } : {}),
        ...(scope.trim() ? { scope: scope.trim() } : {}),
        ...(publishedOn ? { publishedOn } : {}),
        ...(emd.trim() ? { emdAmountPaise: Math.round(Number(emd.replace(/[^0-9.]/g, '')) * 100) } : {}),
        ...(emdInstrument ? { emdInstrument: emdInstrument as 'dd' | 'bank_guarantee' | 'online' | 'cheque' | 'exempt' } : {}),
        ...(emdRef.trim() ? { emdReference: emdRef.trim() } : {}),
      });
      const v = r.value as { tenderId?: number };
      if (v?.tenderId) go(`#/tenders/${v.tenderId}`);
      return r;
    });
    if (!ok) return;
  }

  return (
    <Page title="Record a tender" sub="The notice, its deadline in the zone the notice states, and the earnest money.">
      <Card>
        {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
        <Banner kind="info" title="The deadline is an instant with a zone, and both are kept."
                detail="“15:00” in a notice is not 15:00 on our clock. Enter it as the notice states it — 2026-09-20T15:00:00+05:30 — and say which zone that is." />
        <div className="grid two">
          <div>
            <Text name="buyer" label="Who is tendering" value={buyerName} onChange={setBuyerName} required autoFocus />
            <CustomerPickerById name="buyer-customer" label="Is that an existing customer?" customerId={buyerCustomerId}
                                onPick={(v) => setBuyerCustomerId(v)} placeholder="No — or not one of ours" />
            <Text name="reference" label="Their reference" value={reference} onChange={setReference} required />
            <Text name="title" label="What it is for" value={title} onChange={setTitle} required />
          </div>
          <div>
            <Text name="closes-at" label="Closes at" value={closesAt} onChange={setClosesAt} required
                  help="the instant, with its offset: 2026-09-20T15:00:00+05:30" />
            <Text name="closes-tz" label="Stated timezone" value={closesTz} onChange={setClosesTz} required />
            <Text name="published" label="Published on" type="date" value={publishedOn} onChange={setPublishedOn} />
          </div>
        </div>
        <h3 className="section">Earnest money</h3>
        <div className="grid two">
          <div>
            <Text name="emd" label="Amount (₹)" value={emd} onChange={setEmd} />
            <Select name="emd-instrument" label="How" value={emdInstrument} onChange={setEmdInstrument} placeholder="—"
                    options={[{ value: 'dd', label: 'Demand draft' }, { value: 'bank_guarantee', label: 'Bank guarantee' },
                              { value: 'online', label: 'Online' }, { value: 'cheque', label: 'Cheque' }, { value: 'exempt', label: 'Exempt' }]} />
          </div>
          <div>
            <Text name="emd-ref" label="Reference" value={emdRef} onChange={setEmdRef} />
            <TextArea name="scope" label="Scope" value={scope} onChange={setScope} />
          </div>
        </div>
        <Button kind="primary" onClick={() => { void submit(); }}
                disabled={runner.busy || !buyerName.trim() || !reference.trim() || !title.trim() || !closesAt || !closesTz.trim()}>
          {runner.busy ? 'Recording…' : 'Record it'}
        </Button>
      </Card>
    </Page>
  );
}

// ============================================================ 0065 — the checklist

/** The words on the bid button, and whether it works: a mandatory line still pending holds it. */
export function bidButton(readiness: BidReadiness): { disabled: boolean; label: string } {
  const n = readiness.mandatoryPending.length;
  if (n > 0) return { disabled: true, label: `Record the bid — ${n} mandatory line${n > 1 ? 's' : ''} pending` };
  return { disabled: false, label: 'Record the bid' };
}

/** Is the checklist answered? Said once, above the fold, in the same terms the bid door uses. */
export function ReadinessNote({ readiness }: { readiness: BidReadiness }) {
  if (readiness.total === 0) {
    return <Banner kind="info" title="No requirements are recorded yet." detail="Add what the notice asks for — eligibility, technical, commercial, delivery, documents — so nothing is missed before the bid goes out." />;
  }
  if (readiness.mandatoryPending.length > 0) {
    const names = readiness.mandatoryPending.map((x) => `${x.requirementNo} (${x.description})`).join('; ');
    return <Banner kind="warn" title={`The bid is held: ${readiness.mandatoryPending.length} mandatory line${readiness.mandatoryPending.length > 1 ? 's are' : ' is'} still pending.`}
                   detail={`${names}. A bid cannot be recorded until every mandatory line is assessed.`} />;
  }
  if (readiness.needsOverride) {
    const names = readiness.mandatoryExceptions.map((x) => x.requirementNo).join(', ');
    return <Banner kind="warn" title={`Mandatory line${readiness.mandatoryExceptions.length > 1 ? 's' : ''} ${names} stand${readiness.mandatoryExceptions.length > 1 ? '' : 's'} as an exception.`}
                   detail="The bid can be recorded only with a written override, which is kept on the bid." />;
  }
  return <Banner kind="good" title="The checklist is answered — the bid is ready to record."
                 detail={`${readiness.satisfied} satisfied, ${readiness.notApplicable} not applicable${readiness.optionalPending ? `, ${readiness.optionalPending} optional still open` : ''}.`} />;
}

const CATEGORIES = ['eligibility', 'technical', 'commercial', 'delivery', 'compliance', 'document', 'other'] as const;
const PAPER_KINDS: readonly { value: AttachmentCategory; label: string }[] = [
  { value: 'notice', label: 'Notice' }, { value: 'corrigendum', label: 'Corrigendum' }, { value: 'specification', label: 'Specification / annexure' },
  { value: 'eligibility', label: 'Eligibility evidence' }, { value: 'emd', label: 'EMD evidence' }, { value: 'bid_package', label: 'Bid package' },
  { value: 'acknowledgement', label: 'Acknowledgement' }, { value: 'clarification', label: 'Clarification' }, { value: 'award', label: 'Award letter' },
  { value: 'loss', label: 'Loss / regret letter' }, { value: 'withdrawal', label: 'Withdrawal' }, { value: 'cancellation', label: 'Cancellation' }, { value: 'other', label: 'Other' },
];
const kindLabel = (v: string) => PAPER_KINDS.find((k) => k.value === v)?.label ?? words(v);

function statusTone(status: string): 'good' | 'danger' | 'primary' | 'empty' {
  return status === 'satisfied' ? 'good' : status === 'exception' ? 'danger' : status === 'not_applicable' ? 'empty' : 'primary';
}

/** The checklist: mandatory gaps first, then the rest; superseded versions as history underneath. */
export function RequirementsPanel({ tenderId, lines, readiness, may, onChanged }: {
  tenderId?: number; lines: readonly RequirementLine[]; readiness: BidReadiness; may: boolean; onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<{ id: number; what: 'assess' | 'supersede' } | null>(null);
  const rank = (l: RequirementLine) => (l.status === 'pending' && l.mandatory ? 0 : l.status === 'exception' && l.mandatory ? 1 : l.status === 'pending' ? 2 : 3);
  const current = lines.filter((l) => l.isCurrent).slice().sort((a, b) => rank(a) - rank(b) || a.requirementNo.localeCompare(b.requirementNo));
  const history = lines.filter((l) => !l.isCurrent).slice().sort((a, b) => a.requirementNo.localeCompare(b.requirementNo) || b.version - a.version);

  return (
    <Card title="What the notice asks for" sub={`${readiness.total} line${readiness.total === 1 ? '' : 's'} · ${readiness.satisfied} satisfied · ${readiness.mandatoryPending.length} mandatory pending · ${readiness.mandatoryExceptions.length} exception${readiness.mandatoryExceptions.length === 1 ? '' : 's'}`}
          actions={may && tenderId ? <Button kind="primary" onClick={() => setAdding((v) => !v)}>{adding ? 'Not now' : 'Add a requirement'}</Button> : null}>
      {adding && tenderId ? <AddRequirement tenderId={tenderId} onDone={() => { setAdding(false); onChanged(); }} /> : null}
      {current.length === 0 ? <p className="muted" style={{ marginBottom: 0 }}>Nothing is recorded yet. The notice's eligibility, technical, commercial and delivery conditions each become one numbered line here.</p> : null}
      {current.map((l) => (
        <div key={l.requirementId} className="card-body" style={{ borderTop: '1px solid var(--border)', paddingLeft: 0, paddingRight: 0 }} data-requirement={l.requirementNo}>
          <div className="detail-grid">
            <div><div className="k">Line</div><div className="v"><strong>{l.requirementNo}</strong><div className="muted">{words(l.category)}{l.version > 1 ? ` · version ${l.version}` : ''}</div></div></div>
            <div style={{ gridColumn: 'span 2' }}><div className="k">{l.mandatory ? 'Mandatory' : 'Optional'}</div><div className="v">{l.description}
              {l.dueAt ? <div className="muted">due {instant(l.dueAt)}</div> : null}
              {l.supersedeReason ? <div className="muted">replaces version {l.version - 1}: {l.supersedeReason}</div> : null}</div></div>
            <div><div className="k">Status</div><div className="v"><Pill tone={statusTone(l.status)}>{l.status}</Pill>
              {l.responseNote ? <div className="muted">{l.responseNote}</div> : null}
              {l.assessedAt ? <div className="muted">by user {l.assessedBy} · {instant(l.assessedAt)}</div> : null}</div></div>
            <div><div className="k">Evidence</div><div className="v">
              {l.evidence.length === 0 ? <span className="muted">{l.evidenceCategory ? `wants: ${kindLabel(l.evidenceCategory)}` : 'none filed'}</span>
                : l.evidence.map((e) => <div key={e.attachmentId}><a href={api.attachmentUrl(e.attachmentId)}>{e.displayName}</a>{e.voided ? <span className="muted"> (filed in error)</span> : null}</div>)}
            </div></div>
          </div>
          {may ? (
            <div className="toolbar" style={{ marginTop: 6 }}>
              <Button small onClick={() => setOpen(open?.id === l.requirementId && open.what === 'assess' ? null : { id: l.requirementId, what: 'assess' })}>Assess</Button>
              <Button small onClick={() => setOpen(open?.id === l.requirementId && open.what === 'supersede' ? null : { id: l.requirementId, what: 'supersede' })}>Corrigendum — replace this line</Button>
            </div>
          ) : null}
          {open?.id === l.requirementId && open.what === 'assess' ? <AssessRequirement line={l} onDone={() => { setOpen(null); onChanged(); }} /> : null}
          {open?.id === l.requirementId && open.what === 'supersede' ? <SupersedeRequirement line={l} onDone={() => { setOpen(null); onChanged(); }} /> : null}
          {l.assessments.length > 1 ? (
            <ul className="timeline" style={{ marginTop: 6 }}>
              {l.assessments.map((a, i) => <li key={i}><strong>{words(a.fromStatus)} → {words(a.toStatus)}</strong> by user {a.actor} — {a.note}<div className="when">{instant(a.at)}</div></li>)}
            </ul>
          ) : null}
        </div>
      ))}
      {history.length > 0 ? (
        <>
          <h3 className="section">Superseded versions</h3>
          <ul className="timeline">
            {history.map((l) => (
              <li key={l.requirementId}><strong>{l.requirementNo} version {l.version}</strong> — {l.description} <Pill tone="empty">superseded</Pill>
                {l.responseNote ? <div className="muted">was {words(l.status)}: {l.responseNote}</div> : null}</li>
            ))}
          </ul>
        </>
      ) : null}
    </Card>
  );
}

function AddRequirement({ tenderId, onDone }: { tenderId: number; onDone: () => void }) {
  const [no, setNo] = useState(''); const [category, setCategory] = useState<string>('eligibility');
  const [description, setDescription] = useState(''); const [mandatory, setMandatory] = useState(true);
  const [dueAt, setDueAt] = useState(''); const [evidence, setEvidence] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run((retryKey) => api.command('tender.addRequirement', {
      tenderId, requirementNo: no.trim(), category: category as RequirementCategory, description: description.trim(), mandatory,
      ...(dueAt.trim() ? { dueAt: dueAt.trim() } : {}),
      ...(evidence ? { evidenceCategory: evidence as AttachmentCategory } : {}),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setNo(''); setDescription(''); setDueAt(''); onDone(); }
  }
  return (
    <div className="card-body" style={{ paddingLeft: 0, paddingRight: 0 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <Text name="req-no" label="Line number" value={no} onChange={setNo} required help="as the notice numbers it: E-1, T-4, C-2" autoFocus />
          <Select name="req-category" label="Kind" value={category} onChange={setCategory} options={CATEGORIES.map((c) => ({ value: c, label: words(c) }))} />
          <Check name="req-mandatory" checked={mandatory} onChange={setMandatory} label="Mandatory — the bid cannot go without it" />
        </div>
        <div>
          <TextArea name="req-description" label="What the notice asks for" value={description} onChange={setDescription} />
          <Text name="req-due" label="Due before the tender closes (optional)" value={dueAt} onChange={setDueAt} help="an instant with its zone, e.g. 2026-09-25T15:00:00+05:30" />
          <Select name="req-evidence" label="Answered by which paper" value={evidence} onChange={setEvidence} placeholder="—" options={PAPER_KINDS.map((k) => ({ value: k.value, label: k.label }))} />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !no.trim() || description.trim().length < 3}>
        {runner.busy ? 'Adding…' : 'Add the line'}
      </Button>
    </div>
  );
}

function AssessRequirement({ line, onDone }: { line: RequirementLine; onDone: () => void }) {
  const [status, setStatus] = useState<string>(line.status === 'pending' ? 'satisfied' : 'pending');
  const [note, setNote] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('tender.assessRequirement', {
      requirementId: line.requirementId, status: status as RequirementLine['status'] as 'pending' | 'satisfied' | 'exception' | 'not_applicable', note: note.trim() }));
    if (ok) onDone();
  }
  return (
    <div className="card-body" style={{ paddingLeft: 0, paddingRight: 0 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <Select name="assess-status" label={`Assess ${line.requirementNo}`} value={status} onChange={setStatus}
                options={[{ value: 'satisfied', label: 'Satisfied — we have it' }, { value: 'exception', label: 'Exception — we do not, and the bid may still go' },
                          { value: 'not_applicable', label: 'Not applicable to us' }, { value: 'pending', label: 'Back to pending' }]} />
        <TextArea name="assess-note" label="What was checked, and what was found" value={note} onChange={setNote} />
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !note.trim()}>{runner.busy ? 'Recording…' : 'Record the assessment'}</Button>
    </div>
  );
}

function SupersedeRequirement({ line, onDone }: { line: RequirementLine; onDone: () => void }) {
  const [description, setDescription] = useState(line.description); const [reason, setReason] = useState('');
  const [mandatory, setMandatory] = useState(line.mandatory);
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run((retryKey) => api.command('tender.supersedeRequirement', {
      requirementId: line.requirementId, reason: reason.trim(), description: description.trim(), mandatory }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) onDone();
  }
  return (
    <div className="card-body" style={{ paddingLeft: 0, paddingRight: 0 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="info" title={`This replaces ${line.requirementNo} with version ${line.version + 1}.`} detail="The earlier version stays on record, and the new one starts pending: what was satisfied against the old wording is not thereby satisfied against the new." />
      <div className="grid two">
        <div>
          <TextArea name="sup-description" label="What the notice now asks for" value={description} onChange={setDescription} />
          <Check name="sup-mandatory" checked={mandatory} onChange={setMandatory} label="Mandatory" />
        </div>
        <TextArea name="sup-reason" label="Why — the corrigendum, usually" value={reason} onChange={setReason} />
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !reason.trim() || description.trim().length < 3}>{runner.busy ? 'Replacing…' : 'Replace the line'}</Button>
    </div>
  );
}

// ============================================================ 0065 — the papers

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** Refuse before a byte is sent: the door would refuse too, after the whole upload. */
export function checkFileBeforeUpload(file: { name: string; size: number; type: string }): string | null {
  if (file.size === 0) return `"${file.name}" is empty — nothing to file.`;
  if (file.size > MAX_UPLOAD_BYTES) return `"${file.name}" is ${sizeOf(file.size)}; a tender document may be at most 10 MiB.`;
  return null;
}

function sizeOf(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
}

/** The file as the command wants it: the base64 after the data-URL prefix. */
function readAsBase64(file: Blob): Promise<string> {
  return new Promise((ok, fail) => {
    const reader = new FileReader();
    reader.onerror = () => fail(new Error('the file could not be read'));
    reader.onload = () => { const s = String(reader.result ?? ''); ok(s.slice(s.indexOf(',') + 1)); };
    reader.readAsDataURL(file);
  });
}

const GROUPS: readonly { label: string; kinds: readonly string[] }[] = [
  { label: 'The notice and its corrigenda', kinds: ['notice', 'corrigendum', 'specification', 'clarification'] },
  { label: 'What we filed', kinds: ['eligibility', 'emd', 'bid_package'] },
  { label: 'Acknowledgement', kinds: ['acknowledgement'] },
  { label: 'Outcome', kinds: ['award', 'loss', 'withdrawal', 'cancellation'] },
  { label: 'Other', kinds: ['other'] },
];

/** Every paper filed, grouped; each says its size, its hash, who filed it and when, and whether it still stands. */
export function DocumentsPanel({ tenderId, files, live, lines, may, onChanged }: {
  tenderId?: number; files: readonly FiledPaper[]; live: number; lines: readonly RequirementLine[]; may: boolean; onChanged: () => void;
}) {
  // nothing filed yet and allowed to file: the form is open, so the first paper is one step away
  const [adding, setAdding] = useState(may && files.length === 0);
  const [open, setOpen] = useState<{ id: number; what: 'void' | 'replace' } | null>(null);
  const byName = new Map(lines.map((l) => [l.requirementId, l.requirementNo] as const));
  return (
    <Card title="The papers" sub={`${files.length} filed · ${live} current`}
          actions={may ? <Button kind="primary" onClick={() => setAdding((v) => !v)}>{adding ? 'Not now' : 'Add document'}</Button> : null}>
      {adding && may ? <UploadDocument tenderId={tenderId ?? 0} lines={lines} onDone={() => { setAdding(false); onChanged(); }} /> : null}
      {files.length === 0 ? <p className="muted" style={{ marginBottom: 0 }}>Nothing is filed yet. The notice, the corrigenda, the bid package, the acknowledgement and the outcome each belong here.</p> : null}
      {GROUPS.map((g) => {
        const rows = files.filter((f) => g.kinds.includes(f.category));
        if (rows.length === 0) return null;
        return (
          <div key={g.label}>
            <h3 className="section">{g.label}</h3>
            {rows.map((f) => (
              <div key={f.attachmentId} className="card-body" style={{ borderTop: '1px solid var(--border)', paddingLeft: 0, paddingRight: 0 }} data-attachment={f.attachmentId}>
                <div className="detail-grid">
                  <div style={{ gridColumn: 'span 2' }}><div className="k">{kindLabel(f.category)}</div><div className="v">
                    <a href={f.downloadPath}><strong>{f.displayName}</strong></a>
                    {' '}{f.voided ? <Pill tone="danger">filed in error</Pill> : f.superseded ? <Pill tone="empty">superseded</Pill> : null}
                    <div className="muted">{sizeOf(f.byteLength)}</div>
                    {f.requirementId !== null ? <div className="muted">answers {byName.get(f.requirementId) ?? `requirement #${f.requirementId}`}</div> : null}
                    {f.supersedesId !== null ? <div className="muted">replaces #{f.supersedesId}</div> : null}
                    {f.supersededById !== null ? <div className="muted">replaced by #{f.supersededById}</div> : null}
                    {f.voided ? <div className="muted">filed in error by user {f.voidedBy} · {instant(f.voidedAt)} — {f.voidReason}</div> : null}
                  </div></div>
                  <div><div className="k">Dated</div><div className="v">{date(f.businessOn)}{f.sourceNote ? <div className="muted">{f.sourceNote}</div> : null}</div></div>
                  <div><div className="k">Filed</div><div className="v">by user {f.uploadedBy}<div className="muted">{instant(f.uploadedAt)}</div></div></div>
                </div>
                {may && tenderId && !f.voided && !f.superseded ? (
                  <div className="toolbar" style={{ marginTop: 6 }}>
                    <Button small onClick={() => setOpen(open?.id === f.attachmentId && open.what === 'replace' ? null : { id: f.attachmentId, what: 'replace' })}>Replace with a newer file</Button>
                    <Button small onClick={() => setOpen(open?.id === f.attachmentId && open.what === 'void' ? null : { id: f.attachmentId, what: 'void' })}>Mark as filed in error</Button>
                  </div>
                ) : null}
                {open?.id === f.attachmentId && open.what === 'void' ? <VoidDocument file={f} onDone={() => { setOpen(null); onChanged(); }} /> : null}
                {open?.id === f.attachmentId && open.what === 'replace' && tenderId ? <UploadDocument tenderId={tenderId} lines={lines} replaces={f} onDone={() => { setOpen(null); onChanged(); }} /> : null}
              </div>
            ))}
          </div>
        );
      })}
    </Card>
  );
}

function UploadDocument({ tenderId, lines, replaces, onDone }: { tenderId: number; lines: readonly RequirementLine[]; replaces?: FiledPaper; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState<string>(replaces?.category ?? 'notice');
  const [businessOn, setBusinessOn] = useState(today());
  const [requirementId, setRequirementId] = useState(replaces?.requirementId ? String(replaces.requirementId) : '');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const runner = useRun(MESSAGES);

  function pick(e: { target: { files: FileList | null } }) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setProblem(f ? checkFileBeforeUpload(f) : null);
  }
  async function submit() {
    if (!file || problem) return;
    const ok = await runner.run(async () => {
      const contentBase64 = await readAsBase64(file);
      const body = {
        category: category as AttachmentCategory, originalFilename: file.name, mimeType: file.type || 'application/octet-stream',
        contentBase64, businessOn, ...(note.trim() ? { sourceNote: note.trim() } : {}),
        ...(requirementId ? { requirementId: Number(requirementId) } : {}),
      };
      return replaces
        ? api.command('tender.supersedeDocument', { attachmentId: replaces.attachmentId, ...body })
        : api.command('tender.addDocument', { tenderId, ...body });
    });
    if (ok) onDone();
  }
  return (
    <div className="card-body" style={{ paddingLeft: 0, paddingRight: 0 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {replaces ? <Banner kind="info" title={`This files a newer version of "${replaces.displayName}".`} detail="The earlier file stays on record and says what replaced it." />
                : <h3 className="section">Add document</h3>}
      <div className="grid two">
        <div>
          <div className="field">
            <label htmlFor="doc-file">The file</label>
            <input id="doc-file" name="doc-file" type="file" onChange={pick} aria-describedby="doc-file-help" />
            <div className="help" id="doc-file-help">up to 10 MB; the file is kept exactly as you give it</div>
            {problem ? <div className="bad" role="alert">{problem}</div> : null}
            {file && !problem ? <div className="muted">{file.name} · {sizeOf(file.size)}</div> : null}
          </div>
          <Select name="doc-kind" label="What it is" value={category} onChange={setCategory} options={PAPER_KINDS.map((k) => ({ value: k.value, label: k.label }))} />
        </div>
        <div>
          <Text name="doc-dated" label="Dated — received, prepared or submitted on" type="date" value={businessOn} onChange={setBusinessOn} required />
          <Select name="doc-requirement" label="Answers which line" value={requirementId} onChange={setRequirementId} placeholder="none in particular"
                  options={lines.filter((l) => l.isCurrent).map((l) => ({ value: String(l.requirementId), label: `${l.requirementNo} — ${l.description}` }))} />
          <Text name="doc-note" label="Where it came from (optional)" value={note} onChange={setNote} />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !file || !!problem || !businessOn}>
        {runner.busy ? 'Filing…' : replaces ? 'File the newer version' : 'File it'}
      </Button>
    </div>
  );
}

export function VoidDocument({ file, onDone }: { file: FiledPaper; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('tender.voidDocument', { attachmentId: file.attachmentId, reason: reason.trim() }));
    if (ok) onDone();
  }
  return (
    <div className="card-body" style={{ paddingLeft: 0, paddingRight: 0 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="warn" title={`Mark "${file.displayName}" as filed in error.`} detail="The file stays on record, marked, with your reason. Nothing is deleted." />
      <TextArea name="void-reason" label="Why it was filed in error" value={reason} onChange={setReason} />
      <Button kind="danger" onClick={() => { void submit(); }} disabled={runner.busy || !reason.trim()}>{runner.busy ? 'Marking…' : 'Mark as filed in error'}</Button>
    </div>
  );
}

// ============================================================ 0071 — what it asks for: gas, unit, vessels and ownership

const OWNER_WORDS: Record<string, string> = { company: 'ours', customer: "the buyer's own" };

/**
 * The demand lines. Read exactly like a quotation's — a gas or a product,
 * a quantity in its own unit — through the same `LineEditor` a quotation and
 * an order already use, with one addition: whose cylinders the notice wants.
 * The whole set is restated on save, same as `quote.setLines`.
 */
function TenderDemandPanel({ tenderId, lines, skus, gases, may, onChanged }: {
  tenderId: number; lines: readonly TenderLine[]; skus: readonly SkuRow[]; gases: readonly { name: string; cylinders: number }[]; may: boolean; onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <Card title="What it asks for" sub="gas or product, in its own unit, and whose cylinders the notice wants"
          actions={may ? <Button onClick={() => setEditing((v) => !v)}>{editing ? 'Leave it' : lines.length ? 'Change the lines' : 'Add lines'}</Button> : undefined}>
      {editing ? <EditTenderLines tenderId={tenderId} lines={lines} skus={skus} gases={gases} onDone={() => { setEditing(false); onChanged(); }} /> : (
        <Register
          columns={[
            { key: 'gas', head: 'Gas / product', cell: (l: TenderLine) => l.gasService ?? (l.skuId !== null ? `product #${l.skuId}` : '—') },
            { key: 'qty', head: 'Quantity', right: true, cell: (l: TenderLine) => `${l.qtyRequested} ${l.qtyUnit === 'cylinder' ? 'cyl' : l.qtyUnit}` },
            // 0166 — a notice asking for gas has no cylinder count until the
            // yard picks the lot, and `String(null)` printed the word "null".
            { key: 'vessels', head: 'Cylinders', right: true,
              cell: (l: TenderLine) => l.qty === null ? <span className="muted">settled on loading</span> : String(l.qty) },
            { key: 'owner', head: 'Whose cylinders', cell: (l: TenderLine) => OWNER_WORDS[l.ownerKind] ?? words(l.ownerKind) },
            { key: 'note', head: 'Note', cell: (l: TenderLine) => l.note ?? <span className="muted">—</span> },
          ]}
          rows={lines} empty="Nothing is recorded yet about what this tender asks for." />
      )}
    </Card>
  );
}

function EditTenderLines({ tenderId, lines, skus, gases, onDone }: {
  tenderId: number; lines: readonly TenderLine[]; skus: readonly SkuOption[]; gases: readonly { name: string; cylinders: number }[]; onDone: () => void;
}) {
  const [draft, setDraft] = useState<LineRow[]>(() => lines.length
    ? lines.map((l) => ({ skuId: l.skuId === null ? '' : String(l.skuId), gasService: l.skuId === null ? (l.gasService ?? '') : '',
                          waterCapacityL: l.skuId === null && l.waterCapacityL !== null ? String(l.waterCapacityL) : '',
                          qty: String(l.qtyRequested), unit: (l.qtyUnit === 'm3' || l.qtyUnit === 'kg' ? l.qtyUnit : 'cylinder'),
                          rupees: '', note: l.note ?? '' }))
    : [emptyLine()]);
  const [ownerKind, setOwnerKind] = useState<string>(lines[0]?.ownerKind ?? 'company');
  const runner = useRun(MESSAGES);
  async function submit() {
    const bodies = toLineBodies(draft).map((b) => ({ ...b, ownerKind: ownerKind as 'company' | 'customer' }));
    const ok = await runner.run(() => api.command('tender.setLines', { tenderId, lines: bodies }));
    if (ok) onDone();
  }
  return (
    <div className="card-body" style={{ paddingLeft: 0, paddingRight: 0 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Select name="tender-owner-kind" label="Whose cylinders" value={ownerKind} onChange={setOwnerKind}
              options={[{ value: 'company', label: 'Ours' }, { value: 'customer', label: "The buyer's own" }]} />
      <LineEditor rows={draft} skus={skus} gases={gases} onChange={setDraft} idPrefix="t" />
      <Button kind="primary" wide onClick={() => { void submit(); }} disabled={runner.busy || toLineBodies(draft).length === 0}>
        {runner.busy ? 'Saving…' : 'Save these lines'}
      </Button>
    </div>
  );
}

// ============================================================ 0071 — delivery conditions (D85's own vocabulary)

const DELIVERY_WORDS: Record<string, string> = { our_truck: 'our truck', customer_pickup: 'customer pickup' };

function TenderDeliveryPanel({ tenderId, conditions, buyerCustomerId, session, may, onChanged }: {
  tenderId: number; conditions: TenderDeliveryConditions | null; buyerCustomerId: number | null; session: Session; may: boolean; onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <Card title="Delivery conditions" sub="where it delivers, and how"
          actions={may ? <Button onClick={() => setEditing((v) => !v)}>{editing ? 'Leave it' : conditions ? 'Change it' : 'Record it'}</Button> : undefined}>
      {editing ? <EditDeliveryConditions tenderId={tenderId} conditions={conditions} buyerCustomerId={buyerCustomerId} session={session}
                                         onDone={() => { setEditing(false); onChanged(); }} /> : (
        conditions === null ? <p className="muted" style={{ marginBottom: 0 }}>Nothing is recorded yet.</p> : (
          <div className="detail-grid">
            <div><div className="k">Site</div><div className="v">{conditions.siteName ?? (conditions.siteId !== null ? `site #${conditions.siteId}` : '—')}</div></div>
            {conditions.siteAddress ? <div><div className="k">Address</div><div className="v">{conditions.siteAddress}</div></div> : null}
            <div><div className="k">Delivery</div><div className="v">{conditions.deliveryMode ? DELIVERY_WORDS[conditions.deliveryMode] ?? words(conditions.deliveryMode) : '—'}</div></div>
            {conditions.note ? <div><div className="k">Note</div><div className="v">{conditions.note}</div></div> : null}
          </div>
        )
      )}
    </Card>
  );
}

function EditDeliveryConditions({ tenderId, conditions, buyerCustomerId, session, onDone }: {
  tenderId: number; conditions: TenderDeliveryConditions | null; buyerCustomerId: number | null; session: Session; onDone: () => void;
}) {
  const [siteId, setSiteId] = useState(conditions?.siteId ? String(conditions.siteId) : '');
  const [siteName, setSiteName] = useState(conditions?.siteName ?? '');
  const [siteAddress, setSiteAddress] = useState(conditions?.siteAddress ?? '');
  const [deliveryMode, setDeliveryMode] = useState(conditions?.deliveryMode ?? '');
  const [note, setNote] = useState(conditions?.note ?? '');
  const sites = useAsk(() => (buyerCustomerId ? api.view('sites.list', { customerId: buyerCustomerId }) : Promise.resolve({ rows: [] })), [buyerCustomerId], MESSAGES);
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('tender.setDeliveryConditions', {
      tenderId,
      ...(siteId ? { siteId: Number(siteId) } : {}),
      ...(siteName.trim() ? { siteName: siteName.trim() } : {}),
      ...(siteAddress.trim() ? { siteAddress: siteAddress.trim() } : {}),
      ...(deliveryMode ? { deliveryMode: deliveryMode as 'our_truck' | 'customer_pickup' } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    }));
    if (ok) onDone();
  }
  return (
    <div className="card-body" style={{ paddingLeft: 0, paddingRight: 0 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {buyerCustomerId && !sites.loading && (sites.data?.rows.length ?? 0) > 0 ? (
        <Select name="delivery-site" label="A site already on our books" value={siteId} onChange={setSiteId} placeholder="—"
                options={(sites.data?.rows ?? []).map((s: { siteId: number; name: string }) => ({ value: String(s.siteId), label: s.name }))} />
      ) : null}
      <Text name="delivery-site-name" label="Or the site as the notice names it" value={siteName} onChange={setSiteName} />
      <TextArea name="delivery-site-address" label="Address" value={siteAddress} onChange={setSiteAddress} />
      <Select name="delivery-mode" label="Delivery" value={deliveryMode} onChange={setDeliveryMode} placeholder="—"
              options={[{ value: 'our_truck', label: 'Our truck' }, { value: 'customer_pickup', label: 'Customer pickup' }]} />
      <Text name="delivery-note" label="Note" value={note} onChange={setNote} />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy}>{runner.busy ? 'Saving…' : 'Save'}</Button>
    </div>
  );
}

// ============================================================ 0071 — the buyer-side people (M2-20's shape)

function TenderContactsPanel({ tenderId, contacts, may, onChanged }: {
  tenderId: number; contacts: readonly TenderContact[]; may: boolean; onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  return (
    <Card title="Who to ask" sub="the buyer-side people named against this tender"
          actions={may ? <Button onClick={() => setAdding((v) => !v)}>{adding ? 'Leave it' : 'Add a contact'}</Button> : undefined}>
      {adding ? <AddTenderContact tenderId={tenderId} onDone={() => { setAdding(false); onChanged(); }} /> : null}
      <Register
        columns={[
          { key: 'name', head: 'Name', cell: (c: TenderContact) => <>{c.name}{c.isPrimary ? <Pill tone="primary">primary</Pill> : null}{!c.active ? <Pill tone="empty">inactive</Pill> : null}</> },
          { key: 'role', head: 'Role', cell: (c: TenderContact) => c.role ?? '—' },
          { key: 'phone', head: 'Phone', cell: (c: TenderContact) => c.phone ?? '—' },
          { key: 'email', head: 'Email', cell: (c: TenderContact) => c.email ?? '—' },
          { key: 'deactivate', head: '', cell: (c: TenderContact) => (may && c.active
              ? <DeactivateTenderContact contact={c} onDone={onChanged} /> : null) },
        ]}
        rows={contacts} empty="No one is named against this tender yet." />
    </Card>
  );
}

function AddTenderContact({ tenderId, onDone }: { tenderId: number; onDone: () => void }) {
  const [name, setName] = useState(''); const [role, setRole] = useState('');
  const [phone, setPhone] = useState(''); const [email, setEmail] = useState(''); const [isPrimary, setIsPrimary] = useState(false);
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('tender.addContact', {
      tenderId, name: name.trim(), ...(role.trim() ? { role: role.trim() } : {}),
      ...(phone.trim() ? { phone: phone.trim() } : {}), ...(email.trim() ? { email: email.trim() } : {}),
      ...(isPrimary ? { isPrimary } : {}),
    }));
    if (ok) { setName(''); setRole(''); setPhone(''); setEmail(''); setIsPrimary(false); onDone(); }
  }
  return (
    <div className="card-body" style={{ paddingLeft: 0, paddingRight: 0 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <Text name="contact-name" label="Name" value={name} onChange={setName} required autoFocus />
        <Text name="contact-role" label="Role" value={role} onChange={setRole} />
        <Text name="contact-phone" label="Phone" value={phone} onChange={setPhone} />
        <Text name="contact-email" label="Email" value={email} onChange={setEmail} />
      </div>
      <Check name="contact-primary" checked={isPrimary} onChange={setIsPrimary} label="Primary contact for this tender" />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !name.trim() || (!phone.trim() && !email.trim())}>
        {runner.busy ? 'Adding…' : 'Add the contact'}
      </Button>
    </div>
  );
}

function DeactivateTenderContact({ contact, onDone }: { contact: TenderContact; onDone: () => void }) {
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('tender.updateContact', {
      contactId: contact.contactId, changes: { active: false }, reason: 'no longer the right person to ask' }));
    if (ok) onDone();
  }
  return <Button kind="ghost" onClick={() => { void submit(); }} disabled={runner.busy}>{runner.busy ? '…' : 'Remove'}</Button>;
}

